import { useState } from "react";
import type { ReactNode } from "react";

/**
 * 🧭 試用之路——分幕式 walkthrough（2026-09-23 Captain「新形式實驗」）：
 * 既有 block diagram 對 first-timer 太複雜；改用一幕一事＋大字白話＋脈衝 NEXT 掣，
 * 60 秒行完試用漏斗整條路。純展示零 API；state 自含（step）。
 * 形式規則照 merchant-ui-minimalism：每幕必備搶眼 NEXT 掣（脈衝光暈）、最後幕自動收。
 */

interface Scene {
  icon: string;
  kicker: string;
  title: string;
  body: ReactNode;
}

const SCENES: Scene[] = [
  {
    icon: "🧪",
    kicker: "試用漏斗 · 60 秒行完整條路",
    title: "這是什麼？",
    body: (
      <>
        <p className="text-slate-200 leading-relaxed">
          讓客人<b className="text-emerald-400">免費試用你的 AI 客服三日</b>——試完自動引導佢變成付費客人。
        </p>
        <div className="grid grid-cols-3 gap-3 mt-5">
          {[
            ["100", "則訊息額度"],
            ["72", "小時全功能"],
            ["30", "個同時試用位"],
          ].map(([n, l]) => (
            <div key={l} className="bg-slate-950 rounded-xl border border-slate-700/50 p-4 text-center">
              <p className="text-3xl font-bold text-emerald-400">{n}</p>
              <p className="text-xs text-slate-400 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    icon: "🎣",
    kicker: "第 1 步 · 入口",
    title: "客人點樣入嚟？",
    body: (
      <ol className="space-y-3">
        {[
          ["傾偈", "客人同 AI 傾咗兩三句——AI 剛幫到手"],
          ["伸出", "AI 順勢送出「🎁 免費試用 72 小時」按鈕"],
          ["一撳", "客人撳掣——系統即刻鎖定名額"],
        ].map(([t, d], i) => (
          <li key={i} className="flex items-center gap-3 bg-slate-950 rounded-xl border border-slate-700/50 p-3.5">
            <span className="w-7 h-7 shrink-0 rounded-full bg-emerald-900/50 border border-emerald-700 text-emerald-400 text-sm font-bold flex items-center justify-center">{i + 1}</span>
            <span className="text-sm text-slate-200"><b className="text-emerald-400">{t}</b>　{d}</span>
          </li>
        ))}
      </ol>
    ),
  },
  {
    icon: "🎁",
    kicker: "第 2 步 · 開始試用",
    title: "撳咗之後即時發生",
    body: (
      <>
        <div className="bg-slate-950 rounded-xl border border-slate-700/50 p-4">
          <p className="text-[10px] text-slate-500 mb-1">客人手機即收（系統訊息，四語自動）：</p>
          <p className="text-sm text-emerald-300">「🎁 試用已開始！你有 72 小時（3 日）＋100 則訊息額度……」</p>
        </div>
        <div className="grid grid-cols-2 gap-2.5 mt-4">
          {[
            "一生一次——試過唔會再試到",
            "原子搶位——幾多人同時撳都唔會超發",
            "承諾一律系統出——AI 唔准亂應承",
            "語言跟客人——繁/简/EN/MS 自動",
          ].map((t) => (
            <div key={t} className="bg-slate-950 rounded-lg border border-slate-700/50 px-3 py-2.5 text-xs text-slate-300">
              ✓　{t}
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    icon: "😩",
    kicker: "岔路 · 30 個位滿晒",
    title: "滿額點算？兩條路",
    body: (
      <div className="space-y-3">
        <div className="bg-slate-950 rounded-xl border border-emerald-800/40 p-4">
          <p className="text-sm font-semibold text-emerald-400 mb-1.5">🔔 路 A：排隊（免費）</p>
          <p className="text-xs text-slate-300 leading-relaxed">即派隊位「第 N 位」＋48 小時優先領取權——有位釋放，系統自動派<b>一次性連結</b>畀佢（48 小時內有效，過咗自動輪俾下一位）。</p>
        </div>
        <div className="bg-slate-950 rounded-xl border border-amber-800/40 p-4">
          <p className="text-sm font-semibold text-amber-400 mb-1.5">⚡ 路 B：唔使等（付費）</p>
          <p className="text-xs text-slate-300 leading-relaxed">即刻去 wtsaibot.com/pay 開業——<b>付費永遠唔使排隊</b>（雙掣設計：唔堵死任何一邊）。</p>
        </div>
      </div>
    ),
  },
  {
    icon: "⏰",
    kicker: "主線 · 72 小時內",
    title: "系統全自動跑（零人手）",
    body: (
      <div className="space-y-0">
        {[
          ["T0", "試用開始——額度＋倒數啟動", "text-emerald-400"],
          ["T+48h", "貼心提醒：剩 24 小時＋付款掣＋一句競爭提醒", "text-sky-400"],
          ["T+72h", "未轉化 → 自動斷線釋放裝置（資料全保留，門戶可返睇）", "text-amber-400"],
          ["T+144h", "智能問候：揀客人時區最近嘅 13:00/19:00 先派", "text-sky-400"],
          ["T+10日", "以退為進感謝訊息——之後靜默，觸發式先再升溫", "text-slate-400"],
        ].map(([t, d, c], i, arr) => (
          <div key={t as string} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={`text-[11px] font-mono font-bold ${c} w-14 text-right pt-0.5`}>{t}</span>
              {i < arr.length - 1 && <span className="w-px flex-1 bg-slate-700 my-1" />}
            </div>
            <p className="text-xs text-slate-300 pb-4 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: "💰",
    kicker: "終點 · 轉化",
    title: "錢喺邊收？",
    body: (
      <>
        <div className="bg-slate-950 rounded-xl border border-emerald-800/40 p-4">
          <p className="text-sm text-slate-200 leading-relaxed">
            試用期內<b className="text-emerald-400">任何時刻</b>客人撳「開業付款」→ wtsaibot.com/pay → KPay 收銀台 →
            付款成功<b className="text-emerald-400">即刻原地復活開業</b>（斷線前轉化，零重設零重掃）——名額同時釋放畀排隊下一位。
          </p>
        </div>
        <p className="text-xs text-slate-500 mt-3 leading-relaxed">
          付款頁帶 <code className="text-emerald-400">?ref=</code> 歸因——後台報表分得到「邊個節點帶嚟嘅轉化」。人手確認轉化：<code className="text-emerald-400">POST /admin/trial/convert</code>。
        </p>
      </>
    ),
  },
  {
    icon: "🎛️",
    kicker: "你的控制台",
    title: "全程控制得住",
    body: (
      <>
        <div className="space-y-2.5">
          {[
            ["🎛️ 開關", "⚙️ 設定 → 🧪 試用漏斗 TAB——關閉＝停新領取（試用中照跑），即時生效", "text-rose-300"],
            ["📊 觀察", "同一 TAB 有即時數；詳細報表 GET /admin/trial/report（節點人數＋時距＋歸因）", "text-sky-300"],
            ["🛟 應急", "排隊領取 offer-claim／轉化確認 convert／報表 report——三個端點人手可達", "text-amber-300"],
          ].map(([t, d, c]) => (
            <div key={t} className="bg-slate-950 rounded-xl border border-slate-700/50 p-3.5">
              <p className={`text-sm font-semibold ${c} mb-0.5`}>{t}</p>
              <p className="text-xs text-slate-300 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-4 text-center">
          完整一條路：對話 → 按鈕 → 搶位 → 試用 → 提醒 → 付款 → 開業 ✅
        </p>
      </>
    ),
  },
];

export default function TrialWalkthrough() {
  const [step, setStep] = useState(0);
  const last = step >= SCENES.length - 1;
  const s = SCENES[step];

  return (
    <div className="max-w-2xl mx-auto">
      {/* 進度 dots＋幕號 */}
      <div className="flex items-center justify-center gap-1.5 mb-5">
        {SCENES.map((_, i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            aria-label={`第 ${i + 1} 幕`}
            className={`h-1.5 rounded-full transition-all ${
              i === step ? "w-6 bg-emerald-500" : i < step ? "w-1.5 bg-emerald-800" : "w-1.5 bg-slate-700 hover:bg-slate-600"
            }`}
          />
        ))}
      </div>

      {/* 幕卡片 */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-7 md:p-9">
        <p className="text-[11px] font-mono text-slate-500 mb-3">
          {step + 1} / {SCENES.length}　·　{s.kicker}
        </p>
        <div className="text-5xl mb-4">{s.icon}</div>
        <h2 className="text-2xl font-bold text-slate-100 mb-5">{s.title}</h2>
        {s.body}
      </div>

      {/* 掣區：上一步 ghost＋NEXT 脈衝（最後幕自動收——換 ↺ 重睇） */}
      <div className="flex items-center justify-center gap-3 mt-6">
        {step > 0 && (
          <button
            onClick={() => setStep((p) => p - 1)}
            className="px-5 py-2.5 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500 text-sm transition-colors"
          >
            ← 上一幕
          </button>
        )}
        {!last ? (
          <button
            onClick={() => setStep((p) => Math.min(p + 1, SCENES.length - 1))}
            className="px-8 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-bold rounded-lg transition-colors animate-pulse shadow-[0_0_18px_rgba(16,185,129,0.45)]"
          >
            下一幕 →
          </button>
        ) : (
          <button
            onClick={() => setStep(0)}
            className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium rounded-lg transition-colors"
          >
            ↺ 由頭睇多次
          </button>
        )}
      </div>
    </div>
  );
}
